import { ReactNode, createContext, useContext, useMemo } from 'react'
import type { CSSProperties, PropsWithChildren } from 'react'

import type { DraggableSyntheticListeners, UniqueIdentifier } from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { makeStyles } from '@material-ui/core/styles'

interface Props {
  id: UniqueIdentifier
}

interface Context {
  attributes: Record<string, any>
  listeners: DraggableSyntheticListeners
  ref(node: HTMLElement | null): void
}

const useStyles = makeStyles(() => ({
  SortableItem: {
    display: 'flex',
    justifyContent: 'space-between',
    flexGrow: 1,
    alignItems: 'center',
    backgroundColor: '#fff',
    boxSizing: 'border-box',
    listStyle: 'none',
  },
  DragHandle: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
    touchAction: 'none',
    cursor: 'grab',
    border: 'none',
    outline: 'none',
    appearance: 'none',
    backgroundColor: 'transparent',
    WebkitTapHighlightColor: 'transparent',
    '&:focus-visible': {
      boxShadow: '0 0px 0px 2px #4c9ffe',
    },
  },
}))

const SortableItemContext = createContext<Context>({
  attributes: {},
  listeners: undefined,
  ref() {},
})

export function SortableItem({ children, id }: PropsWithChildren<Props>) {
  const classes = useStyles()
  const { attributes, isDragging, listeners, setNodeRef, setActivatorNodeRef, transform, transition } = useSortable({
    id,
  })
  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef,
    }),
    [attributes, listeners, setActivatorNodeRef],
  )
  const style: CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  }

  return (
    <SortableItemContext.Provider value={context}>
      <li
        className={classes.SortableItem}
        ref={setNodeRef}
        style={style}
      >
        {children}
      </li>
    </SortableItemContext.Provider>
  )
}

export function DragHandle({ children }: { children: ReactNode }) {
  const classes = useStyles()
  const { attributes, listeners, ref } = useContext(SortableItemContext)

  return (
    <div
      className={classes.DragHandle}
      {...attributes}
      {...listeners}
      ref={ref}
    >
      {children}
    </div>
  )
}
